/**
 * The full permission catalog (MASTER_PROMPT.md §10). Every string here is enforced
 * SERVER-SIDE on the corresponding apps/api route - the UI only hides what the server already
 * blocks. Adding a permission = add it here, then check it in the route handler; nothing reads
 * permissions from anywhere else.
 */

export const PLATFORM_PERMISSIONS = [
  // Tenants
  'tenants.create',
  'tenants.read',
  'tenants.update',
  'tenants.suspend',
  'tenants.settlement_mode.update',
  'tenants.channels.manage',
  'tenants.domains.manage',
  'tenants.branding.manage',
  'tenants.client_users.create',
  'tenants.staff_2fa_policy.update',
  // Client Package / API-access data model (docs/ARCHITECTURE_V2.md §6, §12 item 6 - "Admin app
  // UI... Client Package tab, API-access screens"). Both are platform-admin-only actions on a
  // tenant's row (packages/db/src/clientPackage.ts's setApiAccess/setClientPackage are
  // hard-coded to `actorType: 'platform_admin'` for exactly this reason), so these live in the
  // Tenants group here rather than in `TENANT_PERMISSIONS` below.
  'tenants.api_access.manage',
  'tenants.client_package.manage',
  // Billing
  'billing.plans.manage',
  'billing.plans.assign',
  'billing.simulator',
  'billing.invoices.manage',
  'billing.dunning.manage',
  // Money
  'payouts.approve',
  'payouts.create',
  'payouts.view',
  'refunds.process',
  'ledger.view',
  'reconciliation.view',
  'reconciliation.resolve',
  'disputes.view',
  'disputes.manage',
  // Providers
  'providers.manage',
  // Orders / tickets (cross-tenant, for support)
  'orders.search',
  'orders.view',
  'tickets.resend',
  'tickets.view_as_client', // impersonation
  // Admin & roles
  'admins.manage',
  'roles.manage',
  'audit.view',
  'support.impersonate',
  // Notifications
  'notifications.timing.manage',
  'notifications.templates.manage',
  // Platform settings
  'platform_settings.manage',
] as const;

export type PlatformPermission = (typeof PLATFORM_PERMISSIONS)[number];

export const TENANT_PERMISSIONS = [
  // Events / catalog
  'events.manage',
  'events.publish',
  'sessions.manage',
  'ticket_types.manage',
  'seat_maps.manage',
  'promo_codes.manage',
  'add_ons.manage',
  'custom_questions.manage',
  'waitlists.manage',
  // Orders
  'orders.view',
  'orders.refund',
  'orders.export',
  'tickets.resend',
  'tickets.check_in',
  'tickets.transfer',
  // Reports / money
  'reports.view',
  'payouts.view',
  'invoices.view',
  // Staff & settings
  'staff.manage',
  'roles.manage',
  'api_keys.manage',
  'webhooks.manage',
  'branding.manage',
  'templates.manage',
  'domains.manage',
  'payment_accounts.manage',
] as const;

export type TenantPermission = (typeof TENANT_PERMISSIONS)[number];

export type Permission = PlatformPermission | TenantPermission;

export function isPlatformPermission(p: string): p is PlatformPermission {
  return (PLATFORM_PERMISSIONS as readonly string[]).includes(p);
}

export function isTenantPermission(p: string): p is TenantPermission {
  return (TENANT_PERMISSIONS as readonly string[]).includes(p);
}
